/**
 * What the clinic's check-in asks, and whether it runs at all.
 *
 * This is the governance seam the port kit left open: resolveEnabledElements
 * always accepted a profile, and the shell passed null, so every clinic got the
 * catalog defaults. The profile comes from here.
 *
 * A dialog on the inbox rather than a page of its own — it is settings for the
 * screen you are already looking at, and a clinic touches it about twice.
 */

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAuth } from "hooks/useAuth";
import {
  getVisitStartConfig,
  saveVisitStartConfig,
} from "services/visitStartCheckInsService";
import {
  getVisitStartConfigurableElements,
  getVisitStartCoreElements,
} from "features/visitStart/visitStartElementsCatalog";

// `managerConfigurable: false` in the catalog means exactly that. The locked ones
// are shown rather than hidden: a clinic deciding what its intake asks should be
// able to see everything it asks.
const CONFIGURABLE = getVisitStartConfigurableElements();
const CORE = getVisitStartCoreElements();

export default function VisitStartSettingsDialog({
  open,
  onClose,
  clinicId,
  clinicName,
  onSaved,
}) {
  const { user } = useAuth();
  const [loaded, setLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [saveError, setSaveError] = React.useState(null);

  const { control, handleSubmit, reset, formState } = useForm({
    defaultValues: {
      enabled: false,
      ...CONFIGURABLE.reduce((acc, el) => {
        acc[el.elementId] = Boolean(el.defaultEnabled);
        return acc;
      }, {}),
    },
  });
  const { isDirty, isSubmitting } = formState;

  useEffect(() => {
    if (!open || !clinicId) return undefined;
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    setSaveError(null);
    getVisitStartConfig(clinicId)
      .then((profile) => {
        if (cancelled) return;
        // No profile is the pre-configuration state, not "off": the form falls
        // back to the catalog's defaults, which is what a patient scanning an
        // unconfigured clinic's link already gets. `enabled` still starts false —
        // nobody has said yes to putting this in a waiting room yet.
        const selected = Array.isArray(profile?.enabledElements)
          ? new Set(profile.enabledElements)
          : null;
        reset({
          enabled: Boolean(profile?.enabled),
          ...CONFIGURABLE.reduce((acc, el) => {
            acc[el.elementId] = selected
              ? selected.has(el.elementId)
              : Boolean(el.defaultEnabled);
            return acc;
          }, {}),
        });
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[VisitStartSettingsDialog] config load failed:", err);
        setLoadError(
          "Could not load your current settings. Saving now would overwrite them, so please close this and try again.",
        );
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, open, reset]);

  const onSubmit = async (values) => {
    setSaveError(null);
    try {
      await saveVisitStartConfig(clinicId, {
        enabled: values.enabled,
        // Configurable selections only. Core elements are re-added when a session
        // resolves its question set, so storing them would let a later change to
        // which screens are mandatory disagree with every saved profile.
        enabledElements: CONFIGURABLE.filter((el) => values[el.elementId]).map(
          (el) => el.elementId,
        ),
        updatedBy: user?.uid || null,
      });
      reset(values);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("[VisitStartSettingsDialog] config save failed:", err);
      setSaveError(err?.message || "Could not save your settings.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Check-in settings</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          {!loaded ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                          Use check-ins at {clinicName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When this is off, anyone scanning your code is told the
                          clinic is not using them.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", ml: 0 }}
                  />
                )}
              />

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Optional questions
                </Typography>
                <Stack spacing={2}>
                  {CONFIGURABLE.map((el) => (
                    <Controller
                      key={el.elementId}
                      name={el.elementId}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(field.value)}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {el.label}
                              </Typography>
                              {/* staffDescription, not patientDescription: this
                                  reader is choosing a questionnaire, not
                                  answering it, and four rows had no patient copy
                                  at all. Every row has this. */}
                              <Typography variant="body2" color="text.secondary">
                                {el.staffDescription}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: "flex-start", ml: 0 }}
                        />
                      )}
                    />
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* One sentence rather than a titled section of chips. It is a
                  footnote to the list above, and giving it the same visual weight
                  made it read as a second thing to configure. */}
              <Typography variant="body2" color="text.secondary">
                {CORE.map((el, i) => (
                  <React.Fragment key={el.elementId}>
                    {i > 0 ? (i === CORE.length - 1 ? " and " : ", ") : null}
                    <strong>{el.shortLabel || el.label}</strong>
                  </React.Fragment>
                ))}{" "}
                are always asked and cannot be turned off.
              </Typography>

              {saveError ? <Alert severity="error">{saveError}</Alert> : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isSubmitting}
            disabled={!isDirty || Boolean(loadError)}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

VisitStartSettingsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  clinicId: PropTypes.string,
  clinicName: PropTypes.string,
  /** Called after a successful save, so the page can confirm it inline. */
  onSaved: PropTypes.func,
};
